import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  GS,
  SCANNER_THRESHOLD_MS,
  SCANNER_MIN_LENGTH,
  RU_TO_LATIN_MAP,
  analyzeKiz,
  normalizeCodeValue,
  useCameraScanner,
  BARCODE_CAMERA_FORMATS,
  BARCODE_FORMATS,
} from "./chestnyZnakCore";
import { LABEL_SIZES, buildCzLabelHtml, czLabelPreviewMeta, openPrintWindow } from "./chestnyZnakPrint";
import "./BarcodeLabeler.css";
import "./ChestnyZnakLabeler.css";
import "./OrderTape.css";

const TITLES_KEY   = "cz_tape_titles";
const DATES_KEY    = "cz_tape_last_dates";
const BARCODES_KEY = "cz_tape_barcodes";

const todayStr = () => new Date().toISOString().slice(0, 10);

function emptyOrder() {
  return {
    czValue: "",
    title: "",
    dateValues: { mfg: todayStr(), exp: todayStr(), shelf: { value: "", unit: "months" } },
    barcodeValue: "",
  };
}

function loadTitleHistory() {
  try { return JSON.parse(localStorage.getItem(TITLES_KEY) || "[]"); } catch { return []; }
}
function pushTitleHistory(title) {
  const list = loadTitleHistory().filter((t) => t !== title);
  list.unshift(title);
  try { localStorage.setItem(TITLES_KEY, JSON.stringify(list.slice(0, 8))); } catch { /* localStorage недоступен */ }
}
function loadDatesHistory() {
  try { return JSON.parse(localStorage.getItem(DATES_KEY) || "null"); } catch { return null; }
}
function saveDatesHistory(dateValues) {
  try { localStorage.setItem(DATES_KEY, JSON.stringify(dateValues)); } catch { /* localStorage недоступен */ }
}
function loadBarcodeHistory() {
  try { return JSON.parse(localStorage.getItem(BARCODES_KEY) || "[]"); } catch { return []; }
}
function pushBarcodeHistory(value) {
  const list = loadBarcodeHistory().filter((v) => v !== value);
  list.unshift(value);
  try { localStorage.setItem(BARCODES_KEY, JSON.stringify(list.slice(0, 8))); } catch { /* localStorage недоступен */ }
}

const normalizeBarcodeValue = (v) =>
  /[а-яёА-ЯЁ]/.test(v) ? v.split("").map((c) => RU_TO_LATIN_MAP[c] ?? c).join("") : v;

const STEP_TITLES = { cz: "Код Честного знака", title: "Название", dates: "Даты", barcode: "Штрихкод" };
const ATTR_MIN_QUERY_LENGTH = 2;
const ATTR_SEARCH_DEBOUNCE_MS = 350;

// `attributeSearch(query) => Promise<Array<{offer_id, sku, name, barcode}>>` — опционально,
// передаётся только когда лента встроена в маркетплейс-раздел (см. OzonFbsChzPrint), где есть
// база «Атрибуты» для автоподстановки названия и штрихкода. В автономном /free-functions
// инструменте не передаётся — тогда работает как раньше, чисто на ручном вводе и истории.
export default function OrderTape({ settings, onSettingsChange, attributeSearch }) {
  const [running, setRunning] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [order, setOrder] = useState(emptyOrder);
  const [czWarning, setCzWarning] = useState("");
  const [barcodeGenWarning, setBarcodeGenWarning] = useState("");
  const [printedCount, setPrintedCount] = useState(0);

  const czInputRef = useRef(null);
  const czBuffer = useRef([]);
  const czBufferTimer = useRef(null);
  const barcodeInputRef = useRef(null);

  // ─── поиск по «Атрибутам» на шаге «Название» (если подключён) ─────────────
  const [attrQuery, setAttrQuery] = useState("");
  const [attrCandidates, setAttrCandidates] = useState([]);
  const [attrSearching, setAttrSearching] = useState(false);
  const [attrError, setAttrError] = useState("");
  const attrSeqRef = useRef(0);

  useEffect(() => {
    if (!attributeSearch) return;
    const q = attrQuery.trim();
    if (q.length < ATTR_MIN_QUERY_LENGTH) {
      attrSeqRef.current += 1;
      setAttrCandidates([]);
      setAttrSearching(false);
      setAttrError("");
      return;
    }
    const seq = ++attrSeqRef.current;
    setAttrSearching(true);
    setAttrError("");
    const timer = setTimeout(async () => {
      try {
        const items = await attributeSearch(q);
        if (seq !== attrSeqRef.current) return;
        setAttrCandidates(Array.isArray(items) ? items : []);
        if (!items || items.length === 0) setAttrError("Товар не найден в базе «Атрибуты».");
      } catch (e) {
        if (seq !== attrSeqRef.current) return;
        setAttrCandidates([]);
        setAttrError(e?.message || "Ошибка поиска товара.");
      } finally {
        if (seq === attrSeqRef.current) setAttrSearching(false);
      }
    }, ATTR_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [attrQuery, attributeSearch]);

  const applyAttrCandidate = useCallback((item) => {
    setOrder((o) => ({ ...o, title: item.name || o.title, barcodeValue: item.barcode || o.barcodeValue }));
    setAttrQuery("");
    setAttrCandidates([]);
  }, []);

  const steps = useMemo(() => {
    const s = ["cz"];
    if (settings.titleEnabled) s.push("title");
    if (settings.dateFields.mfg || settings.dateFields.shelf || settings.dateFields.exp) s.push("dates");
    if (settings.barcodeEnabled) s.push("barcode");
    return s;
  }, [settings.titleEnabled, settings.dateFields, settings.barcodeEnabled]);

  const currentStep = running ? steps[stepIdx] : null;

  const start = useCallback(() => {
    setOrder(emptyOrder());
    setCzWarning("");
    setBarcodeGenWarning("");
    setAttrQuery("");
    setAttrCandidates([]);
    setStepIdx(0);
    setRunning(true);
  }, []);

  const stop = useCallback(() => setRunning(false), []);

  // ─── завершение заказа: печать + сохранение истории + новый цикл ──────────

  const finishAndPrint = useCallback((finalOrder) => {
    const effectiveTitle = settings.titleEnabled ? finalOrder.title : "";
    const barcodeConverted = settings.barcodeEnabled && finalOrder.barcodeValue
      ? normalizeBarcodeValue(finalOrder.barcodeValue)
      : "";
    const effectiveW = settings.rotated ? settings.labelSize.height : settings.labelSize.width;
    const effectiveH = settings.rotated ? settings.labelSize.width  : settings.labelSize.height;

    const meta = czLabelPreviewMeta({
      title: effectiveTitle, dateFields: settings.dateFields, dateValues: finalOrder.dateValues,
      barcodeConverted, barcodeFormat: settings.barcodeFormat, labelW: effectiveW, labelH: effectiveH,
    });
    if (meta.barcodeError) {
      setBarcodeGenWarning(`Некорректное значение штрихкода для формата ${settings.barcodeFormat} — этикетка не напечатана. Отсканируйте заново.`);
      return;
    }

    const html = buildCzLabelHtml({
      value: finalOrder.czValue, title: effectiveTitle,
      dateFields: settings.dateFields, dateValues: finalOrder.dateValues,
      barcodeConverted, barcodeFormat: settings.barcodeFormat,
      font: settings.font, labelW: effectiveW, labelH: effectiveH, copies: settings.copies,
    });
    openPrintWindow(html);

    if (effectiveTitle) pushTitleHistory(effectiveTitle);
    if (settings.dateFields.mfg || settings.dateFields.shelf || settings.dateFields.exp) saveDatesHistory(finalOrder.dateValues);
    if (settings.barcodeEnabled && finalOrder.barcodeValue) pushBarcodeHistory(finalOrder.barcodeValue);

    setPrintedCount((n) => n + 1);
    setOrder(emptyOrder());
    setCzWarning("");
    setBarcodeGenWarning("");
    setAttrQuery("");
    setAttrCandidates([]);
    setStepIdx(0);
    // running остаётся true — сразу открывается модалка следующего заказа
  }, [settings]);

  const advanceOrFinish = useCallback((patch) => {
    const next = { ...order, ...patch };
    setOrder(next);
    if (stepIdx + 1 >= steps.length) {
      finishAndPrint(next);
    } else {
      setStepIdx((i) => i + 1);
    }
  }, [order, stepIdx, steps.length, finishAndPrint]);

  // ─── шаг «ЧЗ»: сканер (USB + камера) ────────────────────────────────────────

  const finalizeCzScan = useCallback((scanned) => {
    // Проверяем преобразованное значение (после разбора HRI-скобочного формата),
    // а не сырой ввод — иначе валидные коды без настоящих GS-разделителей
    // ложно бракуются (см. тот же фикс в ChestnyZnakLabeler.js).
    const { structureOk } = analyzeKiz(normalizeCodeValue(scanned));
    if (structureOk) {
      setCzWarning("");
      advanceOrFinish({ czValue: scanned });
    } else {
      setOrder((o) => ({ ...o, czValue: scanned }));
      setCzWarning("Код не прошёл проверку структуры КИЗ (GTIN, серийный номер, ключ 91/92) — похоже на неполное или двойное сканирование. Проверьте и отсканируйте заново.");
    }
  }, [advanceOrFinish]);

  const handleCzKeyDown = useCallback((e) => {
    const isGsChar =
      e.keyCode === 29 || e.key === "GroupSeparator" || e.key === "\x1d" || e.key === "F8" ||
      (e.ctrlKey && e.key === "]" && czBuffer.current.length > 0);
    if (isGsChar) {
      e.preventDefault();
      czBuffer.current.push(GS);
      clearTimeout(czBufferTimer.current);
      czBufferTimer.current = setTimeout(() => { czBuffer.current = []; }, 500);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (czBuffer.current.length >= SCANNER_MIN_LENGTH) {
        const buf = czBuffer.current.filter((c) => typeof c === "string");
        czBuffer.current = [];
        clearTimeout(czBufferTimer.current);
        finalizeCzScan(buf.join(""));
      } else {
        czBuffer.current = [];
      }
      return;
    }
    if (e.key.length === 1) {
      const now = Date.now();
      const last = czBuffer.current._lastTime || 0;
      if (now - last < SCANNER_THRESHOLD_MS || czBuffer.current.length === 0) {
        czBuffer.current.push(e.key);
      } else {
        czBuffer.current = [e.key];
      }
      czBuffer.current._lastTime = now;
      clearTimeout(czBufferTimer.current);
      czBufferTimer.current = setTimeout(() => { czBuffer.current = []; }, 500);
    }
  }, [finalizeCzScan]);

  const handleCzCameraResult = useCallback((text) => {
    finalizeCzScan(text);
  }, [finalizeCzScan]);
  const czCamera = useCameraScanner(handleCzCameraResult);

  const handleBarcodeCameraResult = useCallback((text) => {
    advanceOrFinish({ barcodeValue: text });
  }, [advanceOrFinish]);
  const barcodeCamera = useCameraScanner(handleBarcodeCameraResult, BARCODE_CAMERA_FORMATS);

  const titleHistory = loadTitleHistory();
  const datesHistory = loadDatesHistory();
  const barcodeHistory = loadBarcodeHistory();

  const toggleDateField = (id) => onSettingsChange({ dateFields: { ...settings.dateFields, [id]: !settings.dateFields[id] } });

  // ─── render ────────────────────────────────────────────────────────────────

  if (!running) {
    return (
      <div className="cz-tape">
        <div className="cz-tape__idle">
          <div className="cz-tape__idle-title">Лента заказов</div>
          <div className="cz-tape__idle-hint">
            Выберите, что печатать на каждой этикетке заказа, и размер этикетки. Дальше — «Старт»: модалки по очереди
            для каждого выбранного элемента, печать запускается автоматически после последнего шага, и сразу открывается следующий заказ.
          </div>

          <div className="cz-tape__config">
            <div className="cz-tape__config-row">
              <label className="bl__label">Этикетка</label>
              <div className="bl__size-row">
                <select className="bl__select" value={settings.labelSize.id}
                  onChange={(e) => onSettingsChange({ labelSize: LABEL_SIZES.find((s) => s.id === e.target.value), rotated: false })}>
                  {LABEL_SIZES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <button
                  type="button"
                  className={`bl__rotate-btn${settings.rotated ? " bl__rotate-btn--on" : ""}`}
                  onClick={() => onSettingsChange({ rotated: !settings.rotated })}
                  title={settings.rotated ? "Альбомная → книжная" : "Книжная → альбомная"}
                  aria-label="Повернуть этикетку"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 4v6h6"/>
                    <path d="M3.51 15a9 9 0 1 0 .49-4.25"/>
                  </svg>
                </button>
                <input className="bl__input bl__input--copies" type="number" min={1} max={100}
                  value={settings.copies} title="Копий на заказ"
                  onChange={(e) => onSettingsChange({ copies: Math.max(1, parseInt(e.target.value) || 1) })} />
              </div>
            </div>

            <div className="cz-tape__config-row">
              <label className="bl__label">Что печатать на этикетке</label>
              <div className="cz-tape__toggles">
                <span className="cz-tape__toggle cz-tape__toggle--locked">Код Честного знака</span>
                <label className="bl__checkbox-label">
                  <input type="checkbox" checked={settings.titleEnabled}
                    onChange={() => onSettingsChange({ titleEnabled: !settings.titleEnabled })} />
                  Название
                </label>
                <label className="bl__checkbox-label">
                  <input type="checkbox" checked={settings.dateFields.mfg} onChange={() => toggleDateField("mfg")} />
                  Дата производства
                </label>
                <label className="bl__checkbox-label">
                  <input type="checkbox" checked={settings.dateFields.shelf} onChange={() => toggleDateField("shelf")} />
                  Срок годности
                </label>
                <label className="bl__checkbox-label">
                  <input type="checkbox" checked={settings.dateFields.exp} onChange={() => toggleDateField("exp")} />
                  Годен до
                </label>
                <label className="bl__checkbox-label">
                  <input type="checkbox" checked={settings.barcodeEnabled}
                    onChange={() => onSettingsChange({ barcodeEnabled: !settings.barcodeEnabled })} />
                  Штрихкод
                </label>
                {settings.barcodeEnabled && (
                  <select className="bl__select cz__barcode-fmt" value={settings.barcodeFormat}
                    onChange={(e) => onSettingsChange({ barcodeFormat: e.target.value })}>
                    {BARCODE_FORMATS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </select>
                )}
              </div>
            </div>
          </div>

          <div className="cz-tape__idle-steps">
            <span className="cz-tape__idle-steps-label">Шаги мастера:</span>
            {steps.map((id) => <span key={id} className="cz-tape__idle-step">{STEP_TITLES[id]}</span>)}
          </div>
          {printedCount > 0 && <div className="cz-tape__printed">Напечатано за сессию: {printedCount}</div>}
          <button type="button" className="bl__print-btn cz-tape__start-btn" onClick={start}>Старт</button>
        </div>
      </div>
    );
  }

  return (
    <div className="cz-tape">
      {printedCount > 0 && <div className="cz-tape__printed cz-tape__printed--running">Напечатано за сессию: {printedCount}</div>}

      {/* ── camera overlay: ЧЗ ── */}
      {czCamera.open && (
        <div className="bl__cam-overlay" onClick={czCamera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на Data Matrix Честного знака</span>
              <button className="bl__cam-close" onClick={czCamera.stopScan} aria-label="Закрыть">✕</button>
            </div>
            <div className="bl__cam-viewport bl__cam-viewport--matrix">
              <video ref={czCamera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {czCamera.scanned && <div className="bl__cam-result"><span className="bl__cam-check">✓</span> {czCamera.scanned}</div>}
            </div>
            {czCamera.error && <div className="bl__cam-error">{czCamera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── camera overlay: штрихкод ── */}
      {barcodeCamera.open && (
        <div className="bl__cam-overlay" onClick={barcodeCamera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на штрихкод</span>
              <button className="bl__cam-close" onClick={barcodeCamera.stopScan} aria-label="Закрыть">✕</button>
            </div>
            <div className="bl__cam-viewport">
              <video ref={barcodeCamera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {barcodeCamera.scanned && <div className="bl__cam-result"><span className="bl__cam-check">✓</span> {barcodeCamera.scanned}</div>}
            </div>
            {barcodeCamera.error && <div className="bl__cam-error">{barcodeCamera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── wizard modal ── */}
      <div className="cz-tape__overlay">
        <div className="cz-tape__modal">
          <div className="cz-tape__step-head">
            <span className="cz-tape__step-count">Шаг {stepIdx + 1} из {steps.length}</span>
            <button type="button" className="cz-tape__stop-btn" onClick={stop}>Стоп</button>
          </div>
          <div className="cz-tape__step-title">{STEP_TITLES[currentStep]}</div>

          {currentStep === "cz" && (
            <>
              <div className="bl__input-wrap cz__input-wrap--clearable">
                <input
                  ref={czInputRef}
                  className="bl__input bl__input--value"
                  type="text"
                  autoFocus
                  placeholder="Поднесите сканер или вставьте код..."
                  value={order.czValue}
                  onChange={(e) => { setOrder((o) => ({ ...o, czValue: e.target.value })); setCzWarning(""); }}
                  onKeyDown={handleCzKeyDown}
                />
                {order.czValue && (
                  <button className="bl__input-clear" type="button" title="Очистить" aria-label="Очистить поле"
                    onClick={() => { setOrder((o) => ({ ...o, czValue: "" })); setCzWarning(""); czInputRef.current?.focus(); }}>✕</button>
                )}
                <button className="bl__cam-btn" type="button" onClick={czCamera.startScan}
                  title="Сканировать через камеру" aria-label="Открыть сканер камеры">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                </button>
              </div>
              {czWarning && <div className="cz__scan-warning">{czWarning}</div>}
              <div className="cz-tape__actions">
                <button type="button" className="bl__print-btn" disabled={!order.czValue.trim()}
                  onClick={() => finalizeCzScan(order.czValue.trim())}>Далее</button>
              </div>
            </>
          )}

          {currentStep === "title" && (
            <>
              {attributeSearch && (
                <div className="cz-tape__attr-search">
                  <input
                    className="bl__input"
                    type="text"
                    placeholder="Найти по артикулу, SKU, штрихкоду или названию…"
                    value={attrQuery}
                    onChange={(e) => setAttrQuery(e.target.value)}
                    autoComplete="off"
                  />
                  {attrSearching && <div className="muted" style={{ fontSize: 12 }}>Поиск…</div>}
                  {!attrSearching && attrError && <div className="cz__scan-warning">{attrError}</div>}
                  {!attrSearching && attrCandidates.length > 0 && (
                    <div className="cz-tape__history-list">
                      {attrCandidates.map((c) => (
                        <button key={`${c.offer_id}-${c.sku}`} type="button" className="cz-tape__history-item"
                          onClick={() => applyAttrCandidate(c)}>
                          {c.name || c.offer_id || c.sku}{c.barcode ? ` — ${c.barcode}` : ""}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <input
                className="bl__input"
                type="text"
                autoFocus={!attributeSearch}
                placeholder="Название товара..."
                value={order.title}
                onChange={(e) => setOrder((o) => ({ ...o, title: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") advanceOrFinish({}); }}
              />
              {titleHistory.length > 0 && (
                <div className="cz-tape__history">
                  <span className="cz-tape__history-label">Из ранее заполненных:</span>
                  <div className="cz-tape__history-list">
                    {titleHistory.map((t) => (
                      <button key={t} type="button" className="cz-tape__history-item"
                        onClick={() => setOrder((o) => ({ ...o, title: t }))}>{t}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="cz-tape__actions">
                <button type="button" className="bl__print-btn" onClick={() => advanceOrFinish({})}>Далее</button>
              </div>
            </>
          )}

          {currentStep === "dates" && (
            <>
              {settings.dateFields.mfg && (
                <label className="cz-tape__field-label">
                  Дата производства
                  <input className="bl__input bl__input--date" type="date" value={order.dateValues.mfg}
                    onChange={(e) => setOrder((o) => ({ ...o, dateValues: { ...o.dateValues, mfg: e.target.value } }))} />
                </label>
              )}
              {settings.dateFields.shelf && (
                <label className="cz-tape__field-label">
                  Срок годности
                  <div className="bl__shelf-row">
                    <input className="bl__input bl__input--shelf-num" type="number" min={1} placeholder="число"
                      value={order.dateValues.shelf.value}
                      onChange={(e) => setOrder((o) => ({ ...o, dateValues: { ...o.dateValues, shelf: { ...o.dateValues.shelf, value: e.target.value } } }))} />
                    <select className="bl__select bl__select--shelf" value={order.dateValues.shelf.unit}
                      onChange={(e) => setOrder((o) => ({ ...o, dateValues: { ...o.dateValues, shelf: { ...o.dateValues.shelf, unit: e.target.value } } }))}>
                      <option value="days">дней</option>
                      <option value="months">месяцев</option>
                      <option value="years">лет</option>
                    </select>
                  </div>
                </label>
              )}
              {settings.dateFields.exp && (
                <label className="cz-tape__field-label">
                  Годен до
                  <input className="bl__input bl__input--date" type="date" value={order.dateValues.exp}
                    onChange={(e) => setOrder((o) => ({ ...o, dateValues: { ...o.dateValues, exp: e.target.value } }))} />
                </label>
              )}
              {datesHistory && (
                <button type="button" className="cz-tape__history-item cz-tape__history-item--dates"
                  onClick={() => setOrder((o) => ({ ...o, dateValues: datesHistory }))}>
                  Из ранее заполненных
                </button>
              )}
              <div className="cz-tape__actions">
                <button type="button" className="bl__print-btn" onClick={() => advanceOrFinish({})}>Далее</button>
              </div>
            </>
          )}

          {currentStep === "barcode" && (
            <>
              <div className="cz-tape__field-label">{BARCODE_FORMATS.find((f) => f.id === settings.barcodeFormat)?.label || settings.barcodeFormat}</div>
              <div className="bl__input-wrap cz__input-wrap--clearable">
                <input
                  ref={barcodeInputRef}
                  className="bl__input bl__input--value"
                  type="text"
                  autoFocus
                  placeholder="Введите или поднесите сканер..."
                  value={order.barcodeValue}
                  onChange={(e) => setOrder((o) => ({ ...o, barcodeValue: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === "Enter") advanceOrFinish({}); }}
                />
                {order.barcodeValue && (
                  <button className="bl__input-clear" type="button" title="Очистить" aria-label="Очистить поле"
                    onClick={() => { setOrder((o) => ({ ...o, barcodeValue: "" })); barcodeInputRef.current?.focus(); }}>✕</button>
                )}
                <button className="bl__cam-btn" type="button" onClick={barcodeCamera.startScan}
                  title="Сканировать через камеру" aria-label="Открыть сканер камеры">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                </button>
              </div>
              {barcodeGenWarning && <div className="cz__scan-warning">{barcodeGenWarning}</div>}
              {barcodeHistory.length > 0 && (
                <div className="cz-tape__history">
                  <span className="cz-tape__history-label">Из ранее использованных:</span>
                  <div className="cz-tape__history-list">
                    {barcodeHistory.map((b) => (
                      <button key={b} type="button" className="cz-tape__history-item"
                        onClick={() => setOrder((o) => ({ ...o, barcodeValue: b }))}>{b}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="cz-tape__actions">
                <button type="button" className="bl__print-btn" onClick={() => advanceOrFinish({})}>Печать и следующий заказ</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
